/**
 * CrossCountryCostCompare — turns the multi-country enrichment work
 * into a direct visual buying story.
 *
 * For any degree page, fetches `/api/degrees/{id}/cross-country-cost`
 * and renders a ranked table of the same subject at up to 8 other
 * universities across countries, sorted by cheapest non-EU tuition.
 *
 * Shows the base university row highlighted in-context, with a
 * "You save $X" pill on any row that beats it and a "$X more" pill
 * on rows that don't. Designed to sit inside the Cost section of
 * DegreeDetail.jsx.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, TrendingDown, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => (typeof v === "number" ? `$${v.toLocaleString("en-US")}` : "—");

export default function CrossCountryCostCompare({ degreeId }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(`${API}/api/degrees/${degreeId}/cross-country-cost?limit=8`);
        if (!r.ok) throw new Error("http");
        const j = await r.json();
        if (!cancelled) setState({ loading: false, data: j });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [degreeId]);

  if (state.loading) return null;
  const d = state.data;
  if (!d || !d.eligible) return null;

  const { base, peers, concept_label } = d;

  return (
    <div className="mt-6 rounded-lg border border-indigo-300 bg-indigo-50/40" data-testid="cross-country-cost-root">
      <div className="px-4 py-3 border-b border-indigo-200 bg-white/50">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-indigo-700" />
          <div className="text-[11px] font-underground-sc tracking-wider text-indigo-800">
            Cross-country cost comparison
          </div>
        </div>
        <div className="mt-1 text-sm text-stone-800">
          Same programme — <strong>{concept_label}</strong> — ranked by non-EU
          international tuition across our verified catalogue.
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cross-country-cost-table">
          <thead>
            <tr className="text-left text-[10px] font-underground-sc tracking-wider text-stone-500 border-b border-indigo-200">
              <th className="px-4 py-2">University</th>
              <th className="px-4 py-2">Country</th>
              <th className="px-4 py-2 text-right">EU / Home</th>
              <th className="px-4 py-2 text-right">Intl (non-EU)</th>
              <th className="px-4 py-2 text-right">vs. this uni</th>
            </tr>
          </thead>
          <tbody>
            {/* Base row highlighted */}
            <tr className="bg-indigo-100/60 border-b border-indigo-200" data-testid="cross-country-cost-base-row">
              <td className="px-4 py-2.5 font-semibold text-stone-900">
                <span className="mr-1.5">{base.country_flag}</span>
                {base.university_name}
                <span className="ml-2 rounded-full bg-indigo-700 px-2 py-0.5 text-[9px] font-underground-sc tracking-wider text-white">this uni</span>
              </td>
              <td className="px-4 py-2.5 text-stone-700">{base.city}, {base.country}</td>
              <td className="px-4 py-2.5 text-right font-mono">{fmt(base.tuition_domestic_annual_usd)}</td>
              <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmt(base.tuition_intl_annual_usd)}</td>
              <td className="px-4 py-2.5 text-right text-stone-500 text-xs">baseline</td>
            </tr>
            {peers.map((p) => {
              const saves = p.delta_intl_vs_base > 0;
              const same  = p.delta_intl_vs_base === 0;
              const more  = p.delta_intl_vs_base < 0;
              return (
                <tr key={p.degree_id} className="border-b border-indigo-100/70 hover:bg-white/60" data-testid={`cross-country-cost-peer-${p.country_code}`}>
                  <td className="px-4 py-2.5">
                    <Link to={`/degree/${encodeURIComponent(p.degree_id)}`} className="text-stone-900 hover:underline">
                      <span className="mr-1.5">{p.country_flag}</span>
                      {p.university_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-stone-700">
                    {p.city ? `${p.city}, ${p.country}` : p.country}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(p.tuition_domestic_annual_usd)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(p.tuition_intl_annual_usd)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {saves && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium">
                        <TrendingDown className="h-3 w-3" /> save {fmt(p.delta_intl_vs_base)}
                      </span>
                    )}
                    {same && <span className="text-stone-500 text-xs">same</span>}
                    {more && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-xs font-medium">
                        <TrendingUp className="h-3 w-3" /> {fmt(Math.abs(p.delta_intl_vs_base))} more
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 text-[11px] text-stone-600 border-t border-indigo-100">
        Cheapest peer picked per country. Click any row to open that programme.
      </div>
    </div>
  );
}
